import React, { useEffect, useRef, useState } from 'react';
import Block from '../Block';
import cx from 'classnames';
import './index.less';
import { useScroll } from 'hooks/scroll';
import { useSpring, animated, useTransition, useTrail, useChain } from 'react-spring';
import anime from 'animejs';
import { baseAnimeParams } from '../config';
import { switchList, bgList, cardList } from './config';

export function KeyAdvantages() {
    const [selectIndex, setSelectIndex] = useState(0);
    const mouseOver = useRef(false);
    const animateDone = useRef(false);

    // 卡片切换方向
    // false <-  , true  ->
    const [direction, setDirection] = useState(false);
    const cardTransition = useTransition(selectIndex, (key) => key, {
        from: {
            opacity: 0,
            transform: `translate3d(${direction ? '30%' : '-30%'},0,-20px) scale(0.7)`,
            zIndex: 10,
        },
        enter: { opacity: 1, transform: 'translate3d(0%,0,0) scale(1)', zIndex: 0 },
        leave: {
            opacity: 0,
            transform: `translate3d${direction ? '-30%' : '30%'},0,-20px) scale(0.7)`,
            zIndex: -10,
        },
    });

    useEffect(() => {
        anime({
            targets: ['#block-key-advantages'],
            ...baseAnimeParams,
            opacity: [0, 1],
            translateY: [-40, 0],
        });
    }, []);

    return (
        <Block
            style={{
                opacity: 0,
            }}
            id="block-key-advantages"
            title={
                <h2 id="key-advantages">
                    <a href="#key-advantages">核心优势</a>
                </h2>
            }
            description="KEY ADVANTAGES"
        >
            <div className="key-advantages">
                <KeyAdvantagePath contentId="key-advantages" />
                <div className="switch">
                    {switchList.map((item, index) => (
                        <span
                            className={cx('switch-item', {
                                current: selectIndex === index,
                            })}
                            onClick={() => {
                                setDirection(index - selectIndex > 0);
                                setSelectIndex(index);
                            }}
                            key={index}
                            onAnimationEnd={() => {
                                animateDone.current = true;
                                setTimeout(() => {
                                    setDirection((selectIndex + 1) % 4 > selectIndex);
                                    !mouseOver.current && setSelectIndex((index + 1) % 4);
                                }, 500);
                            }}
                            onAnimationStart={() => {
                                animateDone.current = false;
                            }}
                        >
                            <img src={index === selectIndex ? item.icon : item.hover} />{' '}
                            <span>{item.title}</span>
                        </span>
                    ))}
                </div>
                <div className="content-center">
                    {cardTransition.map(({ props, key, item }) => {
                        return (
                            <Card
                                style={props}
                                key={key}
                                bgImg={React.createElement(bgList[item])}
                                onMouseEnter={() => {
                                    mouseOver.current = true;
                                }}
                                onMouseLeave={() => {
                                    mouseOver.current = false;
                                    setTimeout(() => {
                                        setDirection((selectIndex + 1) % 4 > selectIndex);
                                        animateDone.current &&
                                            setSelectIndex((selectIndex + 1) % 4);
                                    }, 1000);
                                }}
                            >
                                {cardList[item].map((props, index) => {
                                    return (
                                        <CardItem
                                            {...props}
                                            className={cx({
                                                'cur-item': item === selectIndex,
                                            })}
                                            num={index + 1}
                                            key={index}
                                        />
                                    );
                                })}
                            </Card>
                        );
                    })}
                </div>
            </div>
        </Block>
    );
}

export default React.memo(KeyAdvantages);

export function Card(props: {
    bgImg?: React.ReactNode;
    className?: string;
    children?: any;
    style?: React.CSSProperties;
    onMouseOver?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
}) {
    return (
        <animated.div
            style={props.style}
            onMouseOver={props.onMouseOver}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            className={`card ${props.className}`}
        >
            <div className="bg-img">{props.bgImg}</div>
            <div className="bg-canvas">
                <i />
                <i />
                <i />
                <i />
            </div>
            {props.children}
        </animated.div>
    );
}

export function CardItem(props: {
    title: React.ReactNode;
    desc: React.ReactNode;
    className: string;
    num: number;
    style?: React.CSSProperties;
}) {
    const itemRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (wrapRef.current && itemRef.current) {
            anime({
                targets: wrapRef.current.childNodes,
                opacity: [0, 1],
                translateX: [50, 0],
                easing: 'easeOutExpo',
                duration: 1000,
                delay: (el, i) => props.num * 400 + 100 * i,
            });
            anime({
                targets: itemRef.current.querySelector('.border'),
                height: ['0%', '100%'],
                easing: 'linear',
                duration: 400,
                delay: (el, i) => props.num * 400,
            });
        }
    }, []);

    return (
        <div className={`card-item ${props.className}`} ref={itemRef}>
            <div className="border" />
            <div style={props.style} ref={wrapRef}>
                <div className="num">0{props.num}</div>
                <div className="title">{props.title}</div>
                <div className="desc">{props.desc}</div>
            </div>
        </div>
    );
}

export function KeyAdvantagePath(props: { contentId: string }) {
    const [pathOffset, setPathOffset] = useSpring(
        () =>
            ({
                strokeDashoffset: '-101%',
                strokeDasharray: '25% 200%',
                animation: 'none',
            } as React.CSSProperties)
    );

    const domRef = useRef<HTMLDivElement>();
    useEffect(() => {
        domRef.current = document.getElementById(props.contentId) as any;
    }, []);
    const isAnimate = useRef(false);

    useScroll(() => {
        const domOffsetTop = domRef.current.getBoundingClientRect().top;
        const offsetY = window.screen.height - domOffsetTop;
        const isTurnOver = domOffsetTop - 20 < 0;
        if (offsetY > 450 && !isTurnOver) {
            setPathOffset({ strokeDashoffset: `0%` });
            isAnimate.current = true;
        } else if (isTurnOver) {
            setPathOffset({
                strokeDashoffset: `41%`,
                config: {
                    friction: 40,
                },
            });
        } else {
            setPathOffset({ strokeDashoffset: `-101%` });
        }
    }, 100);
    return (
        <div className="road-path">
            <svg width="1369px" height="337px" viewBox="0 0 1369 337">
                <g strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} fillRule="nonzero">
                    <animated.path
                        style={pathOffset}
                        d="M 844.024947 203.251285 L 1344.70423 0.963724372"
                        stroke="#1DD1E2"
                    />
                    <animated.path
                        style={pathOffset}
                        d="M520.549755,114.345426 L0.688117873,325.274074"
                        stroke="#0075FF"
                    />
                </g>
            </svg>
        </div>
    );
}
